
import { useBilling } from './hooks/useBilling';
import { useState } from 'react';

export default function PaymentPage() {
  const { payments, paymentStats } = useBilling();
  const [dateFilter, setDateFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  if (payments.isLoading) return <div className="p-6">Loading payments...</div>;

  const filteredPayments = payments.data?.filter((payment: any) => {
    const matchesSearch = payment.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMethod = methodFilter === 'all' || payment.paymentMethod === methodFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const paymentDate = new Date(payment.paymentDate);
      const today = new Date();
      const daysDiff = Math.floor((today.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case 'today':
          matchesDate = daysDiff === 0;
          break;
        case 'week':
          matchesDate = daysDiff <= 7;
          break;
        case 'month':
          matchesDate = daysDiff <= 30;
          break;
      }
    }
    
    return matchesSearch && matchesMethod && matchesDate;
  }) || [];

  const stats = paymentStats.data || {
    totalPayments: 0,
    totalAmount: 0,
    todayAmount: 0,
    methodBreakdown: {}
  };

  const getPaymentMethodIcon = (method: string) => {
    const icons = {
      cash: 'ri-money-dollar-circle-line',
      card: 'ri-bank-card-line',
      upi: 'ri-smartphone-line',
      bank_transfer: 'ri-bank-line',
      cheque: 'ri-file-text-line'
    };
    return icons[method as keyof typeof icons] || 'ri-money-dollar-circle-line';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Payments</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Total Payments</div>
          <div className="text-2xl font-bold text-[var(--color-primary)]">
            {stats.totalPayments}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600">Total Amount</div>
          <div className="text-2xl font-bold text-[var(--color-primary)]">
            ₹{stats.totalAmount?.toLocaleString() || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600">Today's Collection</div>
          <div className="text-2xl font-bold text-green-600">
            ₹{stats.todayAmount?.toLocaleString() || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600">Average Payment</div>
          <div className="text-2xl font-bold text-blue-600">
            ₹{stats.totalPayments > 0 ? Math.round(stats.totalAmount / stats.totalPayments).toLocaleString() : 0}
          </div>
        </div>
      </div>

      {/* Payment Method Breakdown */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Payment Methods</h2>
        <div className="grid md:grid-cols-5 gap-4">
          {Object.entries(stats.methodBreakdown || {}).map(([method, amount]: [string, any]) => (
            <div key={method} className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-[var(--color-accent)]/20 rounded-full flex items-center justify-center">
                <i className={`${getPaymentMethodIcon(method)} text-[var(--color-primary)] text-xl`}></i>
              </div>
              <div className="text-sm font-medium capitalize">{method.replace('_', ' ')}</div>
              <div className="text-lg font-bold text-[var(--color-primary)]">₹{amount.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="Search by invoice or customer..."
              className="input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              className="input pr-8"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
          <div>
            <select
              className="input pr-8"
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/20">
              <tr>
                <th className="th">Payment ID</th>
                <th className="th">Invoice #</th>
                <th className="th">Customer</th>
                <th className="th">Amount</th>
                <th className="th">Method</th>
                <th className="th">Date</th>
                <th className="th">Transaction ID</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment: any) => (
                  <tr key={payment.id} className="tr">
                    <td className="td">
                      <span className="font-medium">{payment.id}</span>
                    </td>
                    <td className="td">
                      <span className="link">{payment.invoiceNo}</span>
                    </td>
                    <td className="td">
                      <div>
                        <div className="font-medium">{payment.customer}</div>
                        <div className="text-xs text-gray-500">{payment.customerEmail}</div>
                      </div>
                    </td>
                    <td className="td">
                      <span className="font-semibold text-green-600">₹{payment.amount.toLocaleString()}</span>
                    </td>
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <i className={`${getPaymentMethodIcon(payment.paymentMethod)} text-[var(--color-primary)]`}></i>
                        <span className="capitalize">{payment.paymentMethod.replace('_', ' ')}</span>
                      </div>
                    </td>
                    <td className="td">{new Date(payment.paymentDate).toLocaleDateString()}</td>
                    <td className="td">
                      <span className="text-xs text-gray-500">{payment.transactionId || 'N/A'}</span>
                    </td>
                    <td className="td">
                      <div className="flex gap-2">
                        <button
                          className="text-[var(--color-primary)] hover:text-[var(--color-gold)] cursor-pointer"
                          title="View Receipt"
                          onClick={() => {
                            // Receipt generation functionality
                            console.log('Generate receipt for payment:', payment.id);
                          }}
                        >
                          <i className="ri-file-text-line"></i>
                        </button>
                        <button
                          className="text-green-600 hover:text-green-800 cursor-pointer"
                          title="Download Receipt"
                          onClick={() => {
                            // PDF receipt download
                            console.log('Download receipt for payment:', payment.id);
                          }}
                        >
                          <i className="ri-download-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="td text-center text-gray-500 py-8">
                    No payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
